import { Button, Checkbox, CircularProgress, Icon, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, makeStyles, Tooltip } from '@material-ui/core';
import React, { useContext, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { MessageDialog, MsGroupContext } from '..';
import { ChildRoutes, SiteContext, StudentContext, useChildLinks } from '../../contexts';
import { Milestone, MilestoneGroup, Site, User } from '../../dto';
import { useAddMilestoneToPath, useRoadmap } from '../../hooks';
import { LanguageString } from '../Common/LanguageString';

const useStyles = makeStyles(theme => ({
  container: {
    '& > .MuiListItem-container': {
      display: 'flex',
      '& > .MuiListItem-secondaryAction': {
        paddingRight: 'unset'
      },
      '& > .MuiListItemSecondaryAction-root': {
        position: 'unset',
        display: 'flex',
        transform: 'translateY(0)',
        marginRight: '16px'
      }
    }
  }
}));

export interface MsGroupBtns extends MilestoneGroup {
  milestones?: Milestone[];
  filter?: number[];
}

type MilestoneListInnerProps = {
  group: MsGroupBtns;
  student: User;
};

const msName = (ms: Milestone) => {
  return ms.tasks?.[0]?.name;
};

type MSProps = {
  item: Milestone;
  path: string;
  student: User;
  site?: Site;
  handleAddMilestoneClick: (milestone: Milestone) => void;
  handleAddAnother: (milestone: Milestone) => void;
};
const MS: React.FC<MSProps> = ({item, path, student, site, handleAddMilestoneClick, handleAddAnother }) => {
  const msIsInPath = (ms: Milestone) => student.assignments?.assignments?.find(a => a.milestoneId === ms.id) != null;

  return (
    <ListItem key={item.id} component={RouterLink} to={path} button>
      <ListItemIcon>
        <Tooltip title={<LanguageString groupName="ROADMAP" resourceName="MILESTONE_NOT_STARTED" defaultText="Not Started" />}>
          <Icon color="error">error</Icon>
        </Tooltip>
      </ListItemIcon>
      <ListItemText id={String(item.id)}>
        <LanguageString useLabel={true} languageString={msName(item)} />
      </ListItemText>
      {site?.roadmapEnabled ? (
        <ListItemSecondaryAction>
          {item.allowMultipleInstances
            ? msIsInPath(item)
              ? <Button variant="contained" color="primary" style={{ cursor: 'pointer', padding: 0, minWidth: 0 }} onClick={() => handleAddAnother(item)}><Icon>add</Icon></Button>
              : <Checkbox edge="end" checked={msIsInPath(item)} onClick={() => handleAddMilestoneClick(item)} inputProps={{ 'aria-labelledby': String(item.id) }} />
            : <Checkbox id={`ms_checkbox_${item.id}`} edge="end" checked={msIsInPath(item)} onClick={() => handleAddMilestoneClick(item)} inputProps={{ 'aria-labelledby': String(item.id) }} />
          }
        </ListItemSecondaryAction>
      ) : null}
    </ListItem>
  );
};

const MilestoneListInner: React.FC<MilestoneListInnerProps> = ({ group, student }) => {
  const [addMsToPath] = useAddMilestoneToPath();
  const siteCtx = useContext(SiteContext);
  const msgCtx = useContext(MsGroupContext);
  const childLinks = useChildLinks({
    items: group.milestones ? group.milestones : [],
    paramMap: { id: 'msId' },
    routeName: 'milestone'
  });
  const [selectedMs, setSelectedMs] = useState<Milestone | undefined>();
  const [anotherMs, setAnotherMs] = useState<Milestone | undefined>();
  const classes = useStyles();

  const handleAddMilestoneClick = (milestone: Milestone) => setSelectedMs(milestone);

  const handleAddMilestoneConfirm = async (milestone: Milestone) => {
    await addMsToPath({ variables: { input: { milestoneId: milestone.id, userId: student.id, milestoneGroupId: msgCtx?.id } } });
    setSelectedMs(undefined);
  };

  const handleAddAnother = async (milestone: Milestone) => setAnotherMs(milestone);

  const showMilestone = (ms: Milestone) => group.filter == null || group.filter.includes(ms.id);

  const msProps: Omit<MSProps, 'item' | 'path'> = {
    handleAddAnother,
    handleAddMilestoneClick,
    student,
    site: siteCtx.site
  };

  return childLinks ? (
    <List className={classes.container}>
      {childLinks.map(ch =>
        showMilestone(ch.item) ? <MS {...msProps} item={ch.item} path={ch.path} /> : null
      )}
      {(selectedMs != null || anotherMs != null) && (
        <MessageDialog
          message={selectedMs
            ? <LanguageString groupName="ROADMAP" resourceName="ADD_MILESTONE" defaultText="Add this milestone to your roadmap?" />
            : <LanguageString groupName="ROADMAP" resourceName="ADD_ANOTHER_MILESTONE" defaultText="Add another one of these milestones to your roadmap?" />}
          onConfirm={() => handleAddMilestoneConfirm(selectedMs ? selectedMs : anotherMs!)}
        />
      )}
    </List>
  ) : (
    <ChildRoutes />
  );
};

export const MilestoneList: React.FC<{ group: MsGroupBtns }> = props => {
  const studentCtx = useContext(StudentContext);
  const { loading, data: rmData } = useRoadmap({ variables: { userId: studentCtx!.student.id } });

  return loading ? (
    <CircularProgress color="primary" />
  ) : rmData != null && studentCtx?.student != null ? (
    <MilestoneListInner student={studentCtx.student} {...props} />
  ) : null;
};
